import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import KanbanBoard from '../kanban/KanbanBoard'
import GymList from '../gyms/GymList'
import PreRekiChecklist from '../checklists/PreRekiChecklist'
import axios from 'axios'
import { toast } from 'react-toastify'
import { isAfter, addDays } from 'date-fns'

const COODashboard = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [dashboardData, setDashboardData] = useState({
    totalGyms: 0,
    pendingApprovals: 0,
    completedApprovals: 0,
    recentGyms: [],
    stageBreakdown: {},
    checklistStats: {},
    cooGymsCount: 0,
    thisWeekProgress: 0
  })

  // Modal state for metric details
  const [metricModal, setMetricModal] = useState({
    isOpen: false,
    title: '',
    type: null,
    data: [],
    loading: false
  })

  // Tab configuration with icons
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', shortLabel: 'Home', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg> },
    { id: 'kanban', label: 'View Kanban', shortLabel: 'Kanban', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg> },
    { id: 'gyms', label: 'View All Gyms', shortLabel: 'Gyms', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg> },
    { id: 'pre-reki', label: 'Pre-Reki', shortLabel: 'Pre-Reki', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> },
    { id: 'post-reki', label: 'Post-Reki', shortLabel: 'Post-Reki', icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
  ]

  // Check if user is COO
  if (!user || user.role.level !== 2) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">COO access required</p>
        </div>
      </div>
    )
  }

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)

      const [gymsResponse, checklistResponse, approvalsResponse] = await Promise.all([
        axios.get('/api/gyms/stats/overview'),
        axios.get('/api/checklists/stats/overview'),
        axios.get('/api/approvals/stats/overview')
      ])

      const recentGymsResponse = await axios.get('/api/gyms?limit=5')

      const gymsByStage = gymsResponse.data.gymsByStage || []
      const stageBreakdown = gymsByStage.reduce((acc, stage) => {
        acc[stage.stage] = stage._count
        return acc
      }, {})

      const cooStages = ['New Lead', 'Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Post Reki Approved']
      const cooGymsCount = gymsByStage
        .filter(stage => cooStages.includes(stage.stage))
        .reduce((total, stage) => total + stage._count, 0)

      setDashboardData({
        totalGyms: gymsResponse.data.totalGyms || 0,
        cooGymsCount,
        pendingApprovals: approvalsResponse.data?.pending || 0,
        completedApprovals: approvalsResponse.data?.approved || 0,
        recentGyms: recentGymsResponse.data.gyms || [],
        stageBreakdown,
        checklistStats: checklistResponse.data || {},
        thisWeekProgress: calculateWeeklyProgress(recentGymsResponse.data.gyms || [])
      })
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const calculateWeeklyProgress = (gyms) => {
    const oneWeekAgo = addDays(new Date(), -7)
    return gyms.filter(gym => isAfter(new Date(gym.updatedAt), oneWeekAgo)).length
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return isNaN(date) ? 'N/A' : date.toLocaleDateString()
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    return 'Good Evening'
  }

  const getStageColor = (stage) => {
    const colors = {
      'New Lead': 'bg-gray-100 text-gray-800',
      'Site Visit': 'bg-yellow-100 text-yellow-800',
      'Proposal Sent': 'bg-purple-100 text-purple-800',
      'Agreement Signed': 'bg-green-100 text-green-800',
      'Facility Work': 'bg-blue-100 text-blue-800',
      'Post Reki Approved': 'bg-pink-100 text-pink-800',
      'Gym Onboard': 'bg-red-100 text-red-800'
    }
    return colors[stage] || 'bg-gray-100 text-gray-800'
  }

  // Open metric detail modal
  const openMetricModal = async (type, title) => {
    setMetricModal({ isOpen: true, title, type, data: [], loading: true })

    try {
      let data = []

      switch (type) {
        case 'totalGyms':
          const gymsRes = await axios.get('/api/gyms?limit=100')
          data = gymsRes.data.gyms || []
          break

        case 'pendingApprovals':
          const approvalsRes = await axios.get('/api/approvals?status=pending')
          data = approvalsRes.data.approvals || []
          break

        case 'thisWeek':
          const weekGymsRes = await axios.get('/api/gyms?limit=100')
          const oneWeekAgo = addDays(new Date(), -7)
          data = (weekGymsRes.data.gyms || []).filter(gym =>
            isAfter(new Date(gym.updatedAt), oneWeekAgo)
          )
          break

        case 'myScope':
          const scopeGymsRes = await axios.get('/api/gyms?limit=100')
          const cooStages = ['New Lead', 'Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Post Reki Approved']
          data = (scopeGymsRes.data.gyms || []).filter(gym =>
            cooStages.includes(gym.stage)
          )
          break

        default:
          data = []
      }

      setMetricModal(prev => ({ ...prev, data, loading: false }))
    } catch (error) {
      console.error('Error fetching metric details:', error)
      toast.error('Failed to load details')
      setMetricModal(prev => ({ ...prev, loading: false }))
    }
  }

  const closeMetricModal = () => {
    setMetricModal({ isOpen: false, title: '', type: null, data: [], loading: false })
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'kanban':
        return <KanbanBoard />
      case 'gyms':
        return <GymList />
      case 'pre-reki':
        return <PreRekiChecklist />
      case 'post-reki':
        return <PreRekiChecklist />
      default:
        return renderDashboardTab()
    }
  }

  const renderDashboardTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
      {/* Recent Gyms */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Recent Gym Updates</h3>
        <div className="space-y-2 sm:space-y-3">
          {dashboardData.recentGyms.length > 0 ? (
            dashboardData.recentGyms.slice(0, 5).map(gym => (
              <div key={gym.id} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg">
                <div className="flex-1 min-w-0 mr-2">
                  <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{gym.name}</p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">{gym.location}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(gym.stage)}`}>
                    {gym.stage}
                  </span>
                  <p className="text-xs text-gray-500 mt-1 hidden sm:block">
                    {formatDate(gym.updated_at)}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-center py-4 text-sm">No recent gym updates</p>
          )}
        </div>
        <div className="mt-4">
          <button
            onClick={() => setActiveTab('gyms')}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium"
          >
            View all gyms →
          </button>
        </div>
      </div>

      {/* Stage Pipeline Breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Pipeline Breakdown</h3>
        <div className="space-y-2 sm:space-y-3">
          {['New Lead', 'Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Post Reki Approved'].map(stage => {
            const count = dashboardData.stageBreakdown[stage] || 0
            return (
              <div key={stage} className="flex items-center justify-between py-1">
                <div className="flex items-center min-w-0">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3 bg-orange-500 flex-shrink-0"></div>
                  <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">{stage}</span>
                </div>
                <div className="flex items-center flex-shrink-0 ml-2">
                  <span className="text-base sm:text-lg font-bold text-gray-900 mr-1">{count}</span>
                  <span className="text-xs text-gray-500 hidden sm:inline">gyms</span>
                </div>
              </div>
            )
          })}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-xs sm:text-sm font-medium text-gray-900">Your Scope</span>
            <span className="text-base sm:text-lg font-bold text-orange-600">{dashboardData.cooGymsCount}</span>
          </div>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Mobile Optimized */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg text-white p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">
              {getGreeting()}, {user?.name?.split(' ')[0] || 'COO'}!
            </h1>
            <p className="text-orange-100 text-sm sm:text-base mt-1">
              COO Dashboard
            </p>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold">{dashboardData.totalGyms}</div>
              <div className="text-orange-200 text-xs sm:text-sm">Total Gyms</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold">{dashboardData.pendingApprovals}</div>
              <div className="text-orange-200 text-xs sm:text-sm">Pending</div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics - Mobile Grid - Clickable */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
        <div
          onClick={() => openMetricModal('totalGyms', 'Total Gyms')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
        >
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
              </svg>
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{dashboardData.totalGyms}</p>
            </div>
          </div>
        </div>

        <div
          onClick={() => openMetricModal('pendingApprovals', 'Pending Approvals')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6 cursor-pointer hover:shadow-md hover:border-yellow-300 transition-all"
        >
          <div className="flex items-center">
            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Pending</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{dashboardData.pendingApprovals}</p>
            </div>
          </div>
        </div>

        <div
          onClick={() => openMetricModal('thisWeek', 'Updated This Week')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6 cursor-pointer hover:shadow-md hover:border-green-300 transition-all"
        >
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">This Week</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{dashboardData.thisWeekProgress}</p>
            </div>
          </div>
        </div>

        <div
          onClick={() => openMetricModal('myScope', 'My Scope')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6 cursor-pointer hover:shadow-md hover:border-orange-300 transition-all"
        >
          <div className="flex items-center">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">My Scope</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{dashboardData.cooGymsCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Mobile Tab Navigation - Horizontal Scroll Pills */}
        <div className="sm:hidden border-b border-gray-200">
          <div className="flex overflow-x-auto scrollbar-hide p-2 gap-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
                }`}
              >
                {tab.icon}
                <span className="whitespace-nowrap">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Desktop Tab Navigation */}
        <div className="hidden sm:block border-b border-gray-200">
          <div className="flex overflow-x-auto px-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Metric Detail Modal */}
      {metricModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{metricModal.title}</h2>
              <button
                onClick={closeMetricModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {metricModal.loading ? (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : metricModal.data.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-5xl mb-4">📋</div>
                  <p className="text-gray-500">No data available</p>
                </div>
              ) : metricModal.type === 'pendingApprovals' ? (
                // Pending Approvals Table
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gym</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {metricModal.data.map((approval) => (
                        <tr key={approval.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {approval.gym?.name || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {approval.type || 'Stage Transition'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStageColor(approval.gym?.stage)}`}>
                              {approval.gym?.stage || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(approval.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Link
                              to={`/gyms/${approval.gymId}`}
                              className="text-orange-600 hover:text-orange-800 font-medium"
                              onClick={closeMetricModal}
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Gyms Table (for totalGyms, thisWeek, myScope)
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gym Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {metricModal.data.map((gym) => (
                        <tr key={gym.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {gym.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {gym.city}{gym.state ? `, ${gym.state}` : ''}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStageColor(gym.stage)}`}>
                              {gym.stage}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(gym.updatedAt)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Link
                              to={`/gyms/${gym.id}`}
                              className="text-orange-600 hover:text-orange-800 font-medium"
                              onClick={closeMetricModal}
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-xl">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  {metricModal.data.length} {metricModal.data.length === 1 ? 'item' : 'items'}
                </span>
                <button
                  onClick={closeMetricModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default COODashboard
